import {
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import AddressDisplay from 'components/Orders/package/addressDisplay';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useFormikContext } from 'formik';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

const defaultParcel = Object.freeze({
  length: 6,
  width: 8,
  height: 6,
  weight: 16,
});

const toNumberOrNull = (value) => {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const roundCurrency = (value) => Number((Number(value || 0)).toFixed(2));

export default function ShippingInfo() {
  const { values, errors, setFieldValue } = useFormikContext();
  const [rates, setRates] = useState([]);
  const [selectedRates, setSelectedRates] = useState([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [rateError, setRateError] = useState('');

  const packageCount = Math.max(Number(values.shipping?.packageCount || 0), 0);

  const selectedRate = selectedRates[0] || null;
  const includeShipping = Boolean(values.includeShipping);

  const parcels = useMemo(() => (
    Array.from({ length: packageCount || 1 }, () => ({ ...defaultParcel }))
  ), [packageCount]);

  const handleGetRates = useCallback(async () => {
    setIsLoadingRates(true);
    setRateError('');
    setRates([]);
    setSelectedRates([]);
    try {
      const { data } = await httpsCallable(getFunctions(), 'getShippingOrderV2')({
        shipTo: values.addressData,
        parcels,
      });
      setRates(data.rates || []);
    } catch (error) {
      setRateError(error.message);
    } finally {
      setIsLoadingRates(false);
    }
  }, [parcels, values.addressData]);

  const handleSelectRate = useCallback((rate) => {
    const unitPrice = Number(rate.price || 0);
    const calculatedTotal = roundCurrency(unitPrice * packageCount);
    setFieldValue('shipping', {
      carrier: rate.carrier || '',
      service: rate.service || '',
      rateName: rate.name || '',
      displayName: rate.name || '',
      unitPrice,
      packageCount,
      total: calculatedTotal,
    });
  }, [packageCount, setFieldValue]);

  return (
    <Container header={<Header variant="h2">Shipping</Header>}>
      <SpaceBetween size="m">
        <FormField
          label="Shipping Address"
          errorText={errors?.addressData ? 'Address is required' : undefined}
        >
          <AddressDisplay
            address={values.addressData}
            onEdit={(newAddress) => setFieldValue('addressData', newAddress)}
          />
        </FormField>

        <Header
          variant="h3"
          description={
              selectedRate
                ? `Selected rate: ${selectedRate.name} at $${selectedRate.price} x ${packageCount}`
                : 'Add this only when the estimate should include a shipping charge.'
            }
        >
          Shipping Line Item
        </Header>

        <Checkbox
          checked={includeShipping}
          onChange={({ detail }) => {
            setFieldValue('includeShipping', detail.checked);
          }}
        >
          Include shipping line item
        </Checkbox>

        {includeShipping ? (
          <>
            <ColumnLayout columns={2}>
              <FormField
                label="Shipping Name"
                errorText={errors?.shipping?.displayName}
              >
                <Input
                  value={values.shipping?.displayName || ''}
                  onChange={({ detail }) => setFieldValue('shipping.displayName', detail.value)}
                />
              </FormField>

              <FormField
                label="Total Shipping Fee"
                errorText={errors?.shipping?.total}
              >
                <Input
                  type="number"
                  value={values.shipping?.total ?? ''}
                  onChange={({ detail }) => setFieldValue('shipping.total', toNumberOrNull(detail.value))}
                />
              </FormField>

              <FormField
                label="Package Count"
                errorText={errors?.shipping?.packageCount}
              >
                <Input
                  type="number"
                  value={values.shipping?.packageCount ?? ''}
                  onChange={({ detail }) => {
                    const nextPackageCount = toNumberOrNull(detail.value);
                    setFieldValue('shipping.packageCount', nextPackageCount);
                    if (values.shipping?.unitPrice != null) {
                      const calculatedTotal = roundCurrency(
                        values.shipping.unitPrice * (nextPackageCount || 0),
                      );
                      setFieldValue('shipping.total', calculatedTotal);
                    }
                  }}
                />
              </FormField>
            </ColumnLayout>

            <Box>
              <Button
                loading={isLoadingRates}
                disabled={!values.addressData?.street1 || !packageCount}
                onClick={handleGetRates}
                formAction="none"
              >
                Get Shipping Rates
              </Button>
            </Box>

            {rateError ? (
              <Box color="text-status-error">
                {rateError}
              </Box>
            ) : null}

            {rates.length > 0 && (
              <Table
                header={<Header variant="h3">Select Shipping Rate</Header>}
                items={rates}
                selectionType="single"
                trackBy="id"
                selectedItems={selectedRates}
                onSelectionChange={({ detail }) => {
                  setSelectedRates(detail.selectedItems);
                  if (detail.selectedItems[0]) {
                    handleSelectRate(detail.selectedItems[0]);
                  }
                }}
                columnDefinitions={[
                  {
                    id: 'carrier',
                    header: 'Carrier',
                    cell: (item) => item.carrier,
                  },
                  {
                    id: 'service',
                    header: 'Service',
                    cell: (item) => item.name,
                  },
                  {
                    id: 'days',
                    header: 'Transit Days',
                    cell: (item) => item.delivery_days,
                  },
                  {
                    id: 'unitPrice',
                    header: 'Rate',
                    cell: (item) => `$${item.price}`,
                  },
                  {
                    id: 'total',
                    header: 'Calculated Total',
                    cell: (item) => `$${roundCurrency(Number(item.price || 0) * packageCount)}`,
                  },
                ]}
              />
            )}
          </>
        ) : null}
      </SpaceBetween>
    </Container>
  );
}
